import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserContext } from 'stores/user';
import type { TrackingEventData, TrackingEventName } from 'types/tracking';
import { captureAnalyticsEvent } from 'utils/captureAnalyticsEvent';

type TrackingData = {
    [key: string]: unknown;
};

export const useTracking = () => {
    const posthog = usePostHog();
    const { user } = useUserContext();
    const location = useLocation();

    /**
     * Low-level tracking function (for backward compatibility)
     * Prefer using `trackEvent` for type-safe tracking
     */
    const track = useCallback(
        (eventName: string, stepName?: string, additionalData?: TrackingData) => {
            captureAnalyticsEvent(posthog, eventName, {
                url: location.pathname + location.search,
                userEmail: user.email,
                userId: user.id,
                teamId: user.team?.id,
                teamName: user.team?.name,
                stepName,
                ...additionalData,
            });
        },
        [posthog, location.pathname, location.search, user.email, user.id, user.team?.id, user.team?.name]
    );

    /**
     * Type-safe tracking function with full autocomplete support
     *
     * @example
     * // Events with data
     * trackEvent('inquiry_modal_opened', { eventId: 1, eventName: 'Q1 Meeting', venueCount: 3 });
     *
     * // Events without data
     * trackEvent('logout');
     *
     * // Events with optional stepName (for step-based tracking)
     * trackEvent('inquiry_step_viewed', { stepNumber: 1, eventId: 123 }, 'Event Details');
     */
    const trackEvent = useCallback(
        <T extends TrackingEventName>(eventName: T, data?: TrackingEventData<T>, stepName?: string): void => {
            track(eventName, stepName, data as TrackingData);
        },
        [track]
    );

    return {
        track,
        trackEvent,
    };
};
